import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Circle, X, ArrowRight, Rocket } from "lucide-react";

import { useTranslation } from "react-i18next";
const STORAGE_KEY = "hisabx.onboarding.dismissed";

export default function OnboardingChecklist() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let cancelled = false;
    api
      .get("/setup/onboarding")
      .then((r) => !cancelled && setData(r.data))
      .catch(() => {
        /* endpoint is DEALER-only; ignore for other roles */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data) return null;
  if (data.all_done && dismissed) return null;

  const doDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* private mode / storage disabled — ignore */
    }
    setDismissed(true);
  };

  return (
    <div
      className="relative overflow-hidden rounded-xl border border-primary/30 bg-gradient-to-br from-primary/[0.08] via-card to-card p-5 shadow-sm"
      data-testid="onboarding-checklist"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-primary/10 blur-3xl"
      />

      <header className="relative mb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
            <Rocket className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-base font-semibold text-foreground">
              {data.all_done ? "You're all set!" : "Get your pump ready"}
            </div>
            <div className="text-xs text-muted-foreground">
              {data.done}/{data.total} completed · {data.percent}%
            </div>
          </div>
        </div>
        {data.all_done && (
          <button
            onClick={doDismiss}
            aria-label={t("components.onboardingchecklist.dismiss_checklist")}
            data-testid="onboarding-dismiss"
            className="rounded-md p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </header>

      {/* Progress bar */}
      <div className="relative mb-4 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all duration-500"
          style={{ width: `${data.percent}%` }}
          data-testid="onboarding-progress-bar"
        />
      </div>

      {/* Task list */}
      <ul className="relative space-y-2">
        {data.tasks.map((t) => (
          <li
            key={t.key}
            className={
              "group flex items-center gap-3 rounded-lg border border-transparent px-3 py-2 transition-colors " +
              (t.done ? "opacity-60" : "hover:border-border hover:bg-muted/40")
            }
            data-testid={`onboarding-task-${t.key}`}
          >
            {t.done ? (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
            ) : (
              <Circle className="h-4 w-4 shrink-0 text-muted-foreground/60" />
            )}
            <div className="flex-1 min-w-0">
              <div className={"text-sm font-medium " + (t.done ? "line-through text-muted-foreground" : "text-foreground")}>
                {t.label}
              </div>
              <div className="truncate text-xs text-muted-foreground">{t.desc}</div>
            </div>
            {!t.done && t.cta && (
              <Link
                to={t.cta.to}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-semibold text-foreground transition-colors hover:border-primary hover:text-primary"
                data-testid={`onboarding-cta-${t.key}`}
              >
                {t.cta.label}
                <ArrowRight className="h-3 w-3" />
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
